import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useNavigate } from 'react-router-dom';

import { FiLock, FiBell, FiAlertTriangle, FiEye, FiPauseCircle, FiTrash2, FiLogOut, FiShield } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';
import { fetchJson } from '../../utils/fetchJson';
import ConfirmationModal from '../../components/admin/ConfirmationModal';

const SETTINGS_CACHE_KEY = 'settings_cache_v1';

const Settings = () => {
  const { user, logout } = useAuth();
  const { socket, updatePresence } = useSocket();
  const navigate = useNavigate();
  const isFetchingRef = useRef(false);

  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    action: null,
    confirmText: 'Confirm',
    isDestructive: false
  });

  const getDefaultSettings = () => ({
    // Privacy Settings
    profileVisibility: 'all',
    showOnlineStatus: true,
    hideProfileFromSearch: false,

    // Notification Settings
    emailNotifications: true,
    pushNotifications: true,
    messages: true,
    likes: true,

    // Security Settings
    twoFactorAuth: false
  });

  const getCachedSettings = useCallback(() => {
    if (typeof window === 'undefined' || !user?.id) return null;
    try {
      const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.userId !== user.id) return null;
      return parsed.data;
    } catch (err) {
      console.warn('Failed to parse cached settings', err);
      return null;
    }
  }, [user?.id]);

  const [settings, setSettings] = useState(() => {
    const defaults = getDefaultSettings();
    const cached = getCachedSettings();
    return cached ? { ...defaults, ...cached } : defaults;
  });

  useEffect(() => {
    if (!user?.id) return;
    const cached = getCachedSettings();
    if (cached) {
      setSettings(prev => ({ ...prev, ...cached }));
    }
  }, [user?.id, getCachedSettings]);

  const persistSettingsCache = useCallback((nextSettings) => {
    if (typeof window === 'undefined' || !user?.id) return;
    try {
      localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify({
        userId: user.id,
        data: nextSettings
      }));
    } catch (err) {
      console.warn('Failed to persist settings cache', err);
    }
  }, [user?.id]);

  const loadSettings = useCallback(async () => {
    if (isFetchingRef.current || !user?.id) return;

    try {
      isFetchingRef.current = true;
      setIsFetching(true);
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/settings'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
      }

      if (response.ok && data?.success) {
        const nextSettings = data?.data?.settings || {};
        setSettings(prev => {
          const updated = {
            ...prev,
            ...nextSettings
          };
          persistSettingsCache(updated);
          return updated;
        });
      } else {
        console.error('Failed to load settings:', data?.message);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
    }
  }, [user?.id, logout, persistSettingsCache]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (settingsToUse = settings) => {
    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      // Remove email and phone from the payload since they are no longer supported
      const { email, phone, ...settingsToSave } = settingsToUse;

      const { response, data } = await fetchJson(buildApiUrl('/api/settings'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settingsToSave)
      });

      if (response.ok && data.success) {
        setSuccess('Settings saved successfully!');
        persistSettingsCache(settingsToUse);

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);

        // Update socket presence immediately
        if (socket && settingsToSave.showOnlineStatus !== undefined) {
          updatePresence(settingsToSave.showOnlineStatus);
        }
      } else {
        setError(data.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    const nextSettings = {
      ...settings,
      [name]: nextValue
    };

    setSettings(nextSettings);

    // Auto-save for checkboxes and select inputs
    if (type === 'checkbox' || e.target.tagName === 'SELECT') {
      handleSave(nextSettings);
    }
  };





  const handleDeactivateProfile = () => {
    setConfirmationModal({
      isOpen: true,
      title: 'Deactivate Profile?',
      message: 'Your profile will be hidden from everyone until you log in again. You will not appear in searches or recommendations. Your data is safe. Are you sure you want to deactivate?',
      action: 'deactivate',
      confirmText: 'Deactivate',
      isDestructive: false
    });
  };

  const handleDeleteAccount = () => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Profile Permanently?',
      message: 'This action cannot be undone. Your profile, messages, and all associated data will be deleted immediately. Are you absolutely sure?',
      action: 'delete',
      confirmText: 'Delete Permanently',
      isDestructive: true
    });
  };

  const handleConfirmAction = async () => {
    const { action } = confirmationModal;

    try {
      setIsProcessing(true);

      const token = localStorage.getItem('token');
      let url = '';
      let method = '';

      if (action === 'deactivate') {
        url = buildApiUrl('/api/settings/account/deactivate');
        method = 'POST';
      } else if (action === 'delete') {
        url = buildApiUrl('/api/settings/account/delete-immediate');
        method = 'DELETE';
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(data.message);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 3000);
      } else {
        setError(data.message || 'Action failed');
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      }
    } catch (err) {
      console.error('Error executing action:', err);
      setError('An error occurred. Please try again.');
      setConfirmationModal(prev => ({ ...prev, isOpen: false }));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Settings Header */}
        <div className="bg-white rounded-3xl shadow-soft p-8 mb-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Settings</h1>
            <p className="text-gray-600 text-lg">Manage your account preferences</p>
            {isFetching && (
              <div className="mt-4 inline-flex items-center space-x-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                <span>Refreshing your latest preferences…</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-8">
          {/* Privacy Settings Card */}
          <div className="bg-white rounded-3xl shadow-soft p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FiLock className="mr-3 text-romantic-500" size={24} />
              Privacy Settings
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Profile Visibility</label>
                <select
                  name="profileVisibility"
                  value={settings.profileVisibility}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
                >
                  <option value="all">Everyone</option>
                  <option value="matches">My Matches Only</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Show Online Status</label>
                  <p className="text-sm text-gray-500">Let others see when you're online</p>
                </div>
                <input
                  type="checkbox"
                  name="showOnlineStatus"
                  checked={settings.showOnlineStatus}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-5 h-5 text-romantic-600 focus:ring-romantic-500 rounded"
                />
              </div>



              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Hide Profile from Search</label>
                  <p className="text-sm text-gray-500">Your profile won't appear in search results</p>
                </div>
                <input
                  type="checkbox"
                  name="hideProfileFromSearch"
                  checked={settings.hideProfileFromSearch}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-5 h-5 text-romantic-600 focus:ring-romantic-500 rounded"
                />
              </div>

              <div className="pt-4 border-t border-gray-100">
                <a
                  href="https://marrycenter.com/app/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-colors group cursor-pointer"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                      <FiShield className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-700">Privacy Policy</h3>
                      <p className="text-sm text-gray-600">Read our detailed privacy policy and data protection guidelines</p>
                    </div>
                  </div>
                  <div className="text-blue-500 group-hover:translate-x-1 transition-transform">
                    <FiEye className="w-5 h-5" />
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Security Settings Card */}
          <div className="bg-white rounded-3xl shadow-soft p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FiLock className="mr-3 text-romantic-500" size={24} />
              Security Settings
            </h2>

            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Two-Factor Authentication</label>
                  <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                </div>
                <input
                  type="checkbox"
                  name="twoFactorAuth"
                  checked={settings.twoFactorAuth}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-5 h-5 text-romantic-600 focus:ring-romantic-500 rounded"
                />
              </div>



            </div>
          </div>

          {/* Notification Settings Card */}
          <div className="bg-white rounded-3xl shadow-soft p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FiBell className="mr-3 text-romantic-500" size={24} />
              Notification Settings
            </h2>

            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Notifications</label>
                  <p className="text-sm text-gray-500">Receive notifications via email</p>
                </div>
                <input
                  type="checkbox"
                  name="emailNotifications"
                  checked={settings.emailNotifications}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-5 h-5 text-romantic-600 focus:ring-romantic-500 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Push Notifications</label>
                  <p className="text-sm text-gray-500">Receive push notifications</p>
                </div>
                <input
                  type="checkbox"
                  name="pushNotifications"
                  checked={settings.pushNotifications}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-5 h-5 text-romantic-600 focus:ring-romantic-500 rounded"
                />
              </div>



              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Messages</label>
                  <p className="text-sm text-gray-500">Notify when you receive new messages</p>
                </div>
                <input
                  type="checkbox"
                  name="messages"
                  checked={settings.messages}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-5 h-5 text-romantic-600 focus:ring-romantic-500 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Profile Likes</label>
                  <p className="text-sm text-gray-500">Notify when someone likes your profile</p>
                </div>
                <input
                  type="checkbox"
                  name="likes"
                  checked={settings.likes}
                  onChange={handleChange}
                  disabled={isFetching || isProcessing}
                  className="w-5 h-5 text-romantic-600 focus:ring-romantic-500 rounded"
                />
              </div>
            </div>
          </div>

          {/* Danger Zone Card */}
          <div className="bg-white rounded-3xl shadow-soft p-8 border-2 border-red-100">
            <h2 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
              <FiAlertTriangle className="mr-3" size={24} />
              Danger Zone
            </h2>

            <div className="space-y-6">
              {/* Deactivate Profile Option */}
              <div className="border border-orange-200 rounded-xl p-6 bg-gradient-to-br from-orange-50 to-amber-50 hover:shadow-md transition-all duration-200">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <FiPauseCircle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Deactivate Profile</h3>
                    <p className="text-sm text-gray-600">
                      Temporarily hide your profile. You can reactivate anytime by logging back in.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDeactivateProfile}
                  disabled={isProcessing}
                  className="w-full sm:w-auto bg-white border border-orange-200 text-orange-700 px-6 py-3 rounded-xl font-medium hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-sm"
                >
                  {isProcessing && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600" />
                  )}
                  <FiPauseCircle className="w-4 h-4" />
                  <span>Deactivate Profile</span>
                </button>
              </div>

              {/* Delete Account Option */}
              <div className="border border-red-200 rounded-xl p-6 bg-gradient-to-br from-red-50 to-rose-50 hover:shadow-md transition-all duration-200">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <FiTrash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Delete Account</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Permanently delete your account and all data. This action cannot be undone.
                    </p>
                    {/* 180-Day Retention Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start space-x-2">
                      <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-xs font-semibold text-amber-800 mb-0.5">Data Retention Notice</p>
                        <p className="text-xs text-amber-700 leading-relaxed">
                          For safety and legal compliance, your profile, messages, and posts will be immediately hidden from other users but securely archived for <strong>180 days</strong> before being permanently purged. During this period, your data may be accessed only if required by law enforcement authorities. After 180 days, all your data will be irreversibly deleted.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isProcessing}
                  className="w-full sm:w-auto bg-white border border-red-200 text-red-700 px-6 py-3 rounded-xl font-medium hover:bg-red-50 hover:border-red-300 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-sm"
                >
                  {isProcessing && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                  )}
                  <FiTrash2 className="w-4 h-4" />
                  <span>Delete Permanently</span>
                </button>
              </div>

              {/* Logout Option */}
              <div className="border border-gray-200 rounded-xl p-6 bg-gradient-to-br from-gray-50 to-slate-50 hover:shadow-md transition-all duration-200">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FiLogOut className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Logout</h3>
                    <p className="text-sm text-gray-600">
                      Sign out of your current session on this device.
                    </p>
                  </div>
                </div>
                <button
                  onClick={logout}
                  disabled={isProcessing}
                  className="w-full sm:w-auto bg-white border border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-sm"
                >
                  <FiLogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>

          {/* Success and Error Messages */}
          <div className="fixed bottom-8 right-8 z-40 flex flex-col items-end space-y-2 pointer-events-none">
            {success && (
              <div className="p-4 bg-green-600 text-white rounded-2xl shadow-2xl animate-fade-in-up pointer-events-auto">
                <p className="text-sm font-semibold">{success}</p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-600 text-white rounded-2xl shadow-2xl animate-fade-in-up pointer-events-auto">
                <p className="text-sm font-semibold">{error}</p>
              </div>
            )}

            {isSaving && (
              <div className="p-4 bg-gray-900 text-white rounded-2xl shadow-2xl animate-fade-in-up flex items-center space-x-3 pointer-events-auto">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white" />
                <p className="text-sm font-semibold">Saving changes...</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        isDestructive={confirmationModal.isDestructive}
        isLoading={isProcessing}
      />
    </>
  );
};

export default Settings;
